import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, FileDown, Package, AlertTriangle, Building2, ScrollText, Plus, RefreshCw } from "lucide-react";
import Layout from "../components/Layout";
import { api, API, formatError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const TABS = [
  { id: "rat", label: "RAT (Art. 30)", icon: ScrollText },
  { id: "docs", label: "Documentos", icon: FileDown },
  { id: "breaches", label: "Brechas", icon: AlertTriangle },
  { id: "processors", label: "Encargados", icon: Building2 },
];

export default function AdminCompliance() {
  const [tab, setTab] = useState("rat");

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="compliance-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-primary">
            <ShieldCheck className="w-5 h-5" />
            <div className="text-sm uppercase tracking-widest">Cumplimiento AEPD · RGPD · AI Act</div>
          </div>
          <h1 className="font-display text-3xl mt-1">Panel de cumplimiento</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Documentación exigida para una inspección de la AEPD, registro de brechas y encargados del tratamiento.
          </p>
        </div>
        <Button asChild className="bg-primary hover:bg-primary/90" data-testid="compliance-export-zip">
          <a href={`${API}/admin/compliance/export-zip`} target="_blank" rel="noreferrer">
            <Package className="w-4 h-4 mr-2" /> Descargar paquete ZIP
          </a>
        </Button>
      </div>

        <div className="flex flex-wrap gap-2 border-b border-border">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`compliance-tab-${t.id}`}
              className={`px-4 py-2 text-sm border-b-2 -mb-px transition-colors ${
                tab === t.id
                  ? "border-primary text-primary font-medium"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <span className="inline-flex items-center gap-2"><t.icon className="w-4 h-4" />{t.label}</span>
            </button>
          ))}
        </div>

        {tab === "rat" && <RatTab />}
        {tab === "docs" && <DocsTab />}
        {tab === "breaches" && <BreachesTab />}
        {tab === "processors" && <ProcessorsTab />}
      </div>
  );
}

/* ------------------ RAT TAB ------------------ */
function RatTab() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/compliance/rat").then(({ data }) => setData(data)).catch((e) => toast.error(formatError(e)));
  }, []);
  if (!data) return <div className="text-muted-foreground">Cargando…</div>;
  const r = data.responsible;
  return (
    <div className="space-y-4" data-testid="rat-tab">
      <Card>
        <CardContent className="pt-6">
          <div className="font-display text-lg mb-2">Responsable del tratamiento</div>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
            <div><span className="text-muted-foreground">Nombre:</span> {r.name}</div>
            <div><span className="text-muted-foreground">NIF:</span> {r.nif}</div>
            <div><span className="text-muted-foreground">Colegiado:</span> {r.colegiado}</div>
            <div><span className="text-muted-foreground">Colegio:</span> {r.college}</div>
            <div className="sm:col-span-2"><span className="text-muted-foreground">Dirección:</span> {r.address}</div>
            <div><span className="text-muted-foreground">Email:</span> {r.email}</div>
            <div><span className="text-muted-foreground">Teléfono:</span> {r.phone}</div>
          </div>
          <div className="text-xs text-muted-foreground mt-3">Versión {data.version} · Generado {new Date(data.generated_at).toLocaleString()}</div>
        </CardContent>
      </Card>

      <div className="grid gap-3">
        {data.activities.map((a) => (
          <details key={a.id} className="rounded-lg border border-border bg-card p-4" data-testid={`rat-${a.id}`}>
            <summary className="cursor-pointer font-medium flex items-center gap-2">
              <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">{a.id}</span>
              {a.name}
            </summary>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm mt-4">
              <div><span className="text-muted-foreground">Finalidad:</span> {a.purpose}</div>
              <div><span className="text-muted-foreground">Base legal:</span> {a.legal_basis}</div>
              <div><span className="text-muted-foreground">Interesados:</span> {a.data_subjects}</div>
              <div><span className="text-muted-foreground">Destinatarios:</span> {a.recipients}</div>
              <div><span className="text-muted-foreground">Transferencias:</span> {a.transfers}</div>
              <div><span className="text-muted-foreground">Retención:</span> {a.retention}</div>
              <div className="sm:col-span-2">
                <span className="text-muted-foreground">Datos:</span>{" "}
                {(a.data_categories || []).map((c) => (
                  <span key={c} className="inline-block text-xs px-2 py-0.5 rounded-full bg-muted mr-1 mb-1">{c}</span>
                ))}
              </div>
              <div className="sm:col-span-2 text-muted-foreground text-xs mt-2">🔒 {a.security}</div>
            </div>
          </details>
        ))}
      </div>
    </div>
  );
}

/* ------------------ DOCS TAB ------------------ */
function DocsTab() {
  const [docs, setDocs] = useState([]);
  useEffect(() => {
    api.get("/admin/compliance/documents").then(({ data }) => setDocs(data.documents || []))
       .catch((e) => toast.error(formatError(e)));
  }, []);
  return (
    <div className="grid sm:grid-cols-2 gap-3" data-testid="docs-tab">
      {docs.map((d) => (
        <Card key={d.filename}>
          <CardContent className="pt-6">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium truncate">{d.title}</div>
                <div className="text-xs text-primary uppercase tracking-wider">{d.regulation}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {Math.round(d.size_bytes / 1024)} KB · Actualizado {new Date(d.modified_at).toLocaleDateString()}
                </div>
              </div>
              <Button size="sm" variant="outline" asChild data-testid={`doc-${d.filename}`}>
                <a href={`${API}/admin/compliance/document/${d.filename}`} target="_blank" rel="noreferrer">
                  <FileDown className="w-4 h-4 mr-1" /> Ver
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
      {docs.length === 0 && <div className="text-muted-foreground">Cargando documentos…</div>}
    </div>
  );
}

/* ------------------ BREACHES TAB ------------------ */
function BreachesTab() {
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    source: "", description: "", data_categories: "", estimated_affected: "",
    severity: "medium", measures: "", is_drill: false,
  });
  const refresh = () => api.get("/admin/breaches").then(({ data }) => setRows(data.breaches || []));
  useEffect(() => { refresh(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/breaches", {
        source: form.source, description: form.description,
        data_categories: form.data_categories.split(",").map((s) => s.trim()).filter(Boolean),
        estimated_affected: form.estimated_affected ? Number(form.estimated_affected) : null,
        severity: form.severity, measures: form.measures, is_drill: form.is_drill,
      });
      toast.success("Brecha registrada");
      setCreating(false);
      setForm({ source: "", description: "", data_categories: "", estimated_affected: "", severity: "medium", measures: "", is_drill: false });
      refresh();
    } catch (err) { toast.error(formatError(err)); }
  };

  const updateStatus = async (id, updates) => {
    try {
      await api.patch(`/admin/breaches/${id}`, updates);
      toast.success("Actualizado");
      refresh();
    } catch (err) { toast.error(formatError(err)); }
  };

  const severityColor = { low: "bg-emerald-100 text-emerald-800", medium: "bg-amber-100 text-amber-800", high: "bg-orange-100 text-orange-800", critical: "bg-red-100 text-red-800" };

  return (
    <div className="space-y-4" data-testid="breaches-tab">
      <div className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">Registro de brechas de seguridad — RGPD Art. 33 (Notificar a AEPD en ≤ 72 h si aplica)</div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={refresh} data-testid="breach-refresh-btn"><RefreshCw className="w-4 h-4 mr-1" /> Actualizar</Button>
          <Button size="sm" onClick={() => setCreating((c) => !c)} data-testid="breach-new-btn">
            <Plus className="w-4 h-4 mr-1" /> {creating ? "Cancelar" : "Nueva brecha"}
          </Button>
        </div>
      </div>

      {creating && (
        <Card>
          <CardContent className="pt-6">
            <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3" data-testid="breach-form">
              <div className="sm:col-span-2">
                <Label>Fuente de detección</Label>
                <Input required value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="Ej. Alerta automática de login" data-testid="breach-source-input" />
              </div>
              <div className="sm:col-span-2">
                <Label>Descripción</Label>
                <Textarea required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="breach-description-input" />
              </div>
              <div>
                <Label>Categorías de datos afectados (separadas por coma)</Label>
                <Input value={form.data_categories} onChange={(e) => setForm({ ...form, data_categories: e.target.value })} placeholder="Salud, Identificativos" data-testid="breach-categories-input" />
              </div>
              <div>
                <Label>Nº aprox. de afectados</Label>
                <Input type="number" value={form.estimated_affected} onChange={(e) => setForm({ ...form, estimated_affected: e.target.value })} data-testid="breach-affected-input" />
              </div>
              <div>
                <Label>Severidad</Label>
                <select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} className="w-full border border-input rounded-md h-10 px-3" data-testid="breach-severity-select">
                  <option value="low">Baja</option>
                  <option value="medium">Media</option>
                  <option value="high">Alta</option>
                  <option value="critical">Crítica</option>
                </select>
              </div>
              <label className="flex items-center gap-2 mt-6">
                <input type="checkbox" checked={form.is_drill} onChange={(e) => setForm({ ...form, is_drill: e.target.checked })} data-testid="breach-drill-checkbox" />
                <span className="text-sm">Es un simulacro</span>
              </label>
              <div className="sm:col-span-2">
                <Label>Medidas adoptadas</Label>
                <Textarea rows={2} value={form.measures} onChange={(e) => setForm({ ...form, measures: e.target.value })} data-testid="breach-measures-input" />
              </div>
              <div className="sm:col-span-2">
                <Button type="submit" data-testid="breach-submit-btn">Registrar</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        {rows.map((b) => (
          <Card key={b.id} data-testid={`breach-${b.id}`}>
            <CardContent className="pt-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${severityColor[b.severity]}`}>{b.severity}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-muted">{b.status}</span>
                    {b.is_drill && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">Simulacro</span>}
                    {b.aepd_notified && <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">AEPD notificada</span>}
                  </div>
                  <div className="font-medium">{b.description}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {new Date(b.detected_at).toLocaleString()} · {b.source}
                    {b.estimated_affected != null && ` · ${b.estimated_affected} afectados`}
                  </div>
                  {(b.data_categories || []).length > 0 && (
                    <div className="mt-1">
                      {b.data_categories.map((c) => (
                        <span key={c} className="text-xs px-2 py-0.5 rounded-full bg-muted mr-1">{c}</span>
                      ))}
                    </div>
                  )}
                  {b.measures && <div className="text-xs text-muted-foreground mt-2">Medidas: {b.measures}</div>}
                </div>
                <div className="flex flex-col gap-1">
                  <select value={b.status} onChange={(e) => updateStatus(b.id, { status: e.target.value })} className="text-xs border rounded px-2 py-1" data-testid={`breach-status-${b.id}`}>
                    <option value="detected">Detectada</option>
                    <option value="contained">Contenida</option>
                    <option value="assessed">Evaluada</option>
                    <option value="notified">Notificada</option>
                    <option value="closed">Cerrada</option>
                  </select>
                  <label className="text-xs flex items-center gap-1">
                    <input type="checkbox" checked={!!b.aepd_notified} onChange={(e) => updateStatus(b.id, { aepd_notified: e.target.checked })} data-testid={`breach-aepd-${b.id}`} />
                    AEPD
                  </label>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {rows.length === 0 && (
          <Card>
            <CardContent className="pt-8 pb-8 text-center text-muted-foreground">
              Sin brechas registradas. 🎉 Recuerda: realiza al menos un simulacro anual.
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

/* ------------------ PROCESSORS TAB ------------------ */
function ProcessorsTab() {
  const [rows, setRows] = useState([]);
  const refresh = () => api.get("/admin/processors").then(({ data }) => setRows(data.processors || []));
  useEffect(() => { refresh(); }, []);

  const toggleDPA = async (id, dpa_signed) => {
    try {
      await api.patch(`/admin/processors/${id}`, { dpa_signed });
      toast.success("Encargado actualizado");
      refresh();
    } catch (err) { toast.error(formatError(err)); }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/processors/${id}`, { status });
      toast.success("Estado actualizado");
      refresh();
    } catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div className="space-y-3" data-testid="processors-tab">
      <div className="text-sm text-muted-foreground">
        Encargados del tratamiento (RGPD Art. 28). Contratos DPA firmados con proveedores que acceden a datos personales.
      </div>
      {rows.map((p) => (
        <Card key={p.id} data-testid={`processor-${p.id}`}>
          <CardContent className="pt-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-medium">{p.name}</div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    p.status === "active" ? "bg-emerald-100 text-emerald-800" :
                    p.status === "review" ? "bg-amber-100 text-amber-800" :
                    "bg-muted"
                  }`}>{p.status}</span>
                  {p.dpa_signed
                    ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200">DPA ✔</span>
                    : <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-800 border border-red-200">Sin DPA</span>}
                  {p.scc_applied && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-800 border border-blue-200">SCC</span>}
                </div>
                <div className="text-sm text-muted-foreground mt-0.5">{p.service}</div>
                <div className="text-xs mt-1"><span className="text-muted-foreground">Región:</span> {p.region || "—"}</div>
                {(p.data_categories || []).length > 0 && (
                  <div className="mt-1">
                    {p.data_categories.map((c) => (
                      <span key={c} className="text-xs px-2 py-0.5 rounded-full bg-muted mr-1">{c}</span>
                    ))}
                  </div>
                )}
                {p.notes && <div className="text-xs text-muted-foreground mt-1 italic">{p.notes}</div>}
                {p.dpa_url && (
                  <a href={p.dpa_url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">
                    Ver contrato DPA ↗
                  </a>
                )}
              </div>
              <div className="flex flex-col gap-1 text-xs">
                <label className="flex items-center gap-1">
                  <input type="checkbox" checked={!!p.dpa_signed} onChange={(e) => toggleDPA(p.id, e.target.checked)} data-testid={`processor-dpa-${p.id}`} />
                  DPA firmado
                </label>
                <select value={p.status} onChange={(e) => setStatus(p.id, e.target.value)} className="border rounded px-2 py-1" data-testid={`processor-status-${p.id}`}>
                  <option value="active">Activo</option>
                  <option value="review">En revisión</option>
                  <option value="inactive">Inactivo</option>
                </select>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
      {rows.length === 0 && <div className="text-muted-foreground">Cargando…</div>}
    </div>
  );
}
